import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Camera,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";

const StepThreeLivePhoto = ({
  formData,
  setFormData,
  prevStep,
  completeRegistration,
  uploadIMG,
  setDoctorPhoto,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [preview, setPreview] = useState(formData.livePhotoUrl || null);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (preview) return;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
          },
        });

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        setError("Unable to access camera. Please allow camera permission.");
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [preview]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d");
    const size = Math.min(video.videoWidth, video.videoHeight);

    if (!size) {
      setError("Camera is still loading. Please wait a few seconds.");
      return;
    }

    canvas.width = size;
    canvas.height = size;

    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;

    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const file = new File([blob], "live-photo.jpg", {
          type: "image/jpeg",
        });

        setFormData((prev) => ({
          ...prev,
          livePhoto: file,
        }));

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }

        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }

        setError("");
        setPreview(URL.createObjectURL(blob));
      },
      "image/jpeg",
      0.9
    );
  };

  const handleRetake = async () => {
    setPreview(null);
    setError("");
    setDoctorPhoto?.(null);

    setFormData((prev) => ({
      ...prev,
      livePhoto: null,
      livePhotoUrl: null,
    }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
        },
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      setError("Camera restart failed. Please allow camera access and try again.");
    }
  };

  const handleSubmit = async () => {
    if (!formData.livePhoto) {
      setError("Please capture your photo first.");
      return;
    }

    try {
      setLoader(true);
      setError("");

      const imageUrl = await uploadIMG(formData.livePhoto);

      if (!imageUrl) {
        setError("Upload failed. Please try again.");
        return;
      }

      setDoctorPhoto?.(imageUrl);
      setFormData((prev) => ({
        ...prev,
        livePhotoUrl: imageUrl,
      }));

      completeRegistration(imageUrl);
    } catch {
      setError("Upload failed. Try again.");
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="doctor-step-body">
      <div className="doctor-camera-card">
        <div className="doctor-camera-frame">
          {!preview ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="doctor-camera-video"
              />
              <div className="doctor-face-guide" aria-hidden="true" />
              <div className="doctor-camera-hint">
                <Camera size={14} />
                Align your face
              </div>
            </>
          ) : (
            <>
              <img
                src={preview}
                alt="Live verification preview"
                className="doctor-camera-preview"
              />
              <div className="doctor-camera-hint is-ready">
                <CheckCircle2 size={14} />
                Photo captured
              </div>
            </>
          )}
        </div>

        <div className="doctor-photo-guidance">
          <div>
            <BadgeCheck size={18} />
            <p>Good lighting</p>
            <small>Face should be clear.</small>
          </div>
          <div>
            <Camera size={18} />
            <p>No filters</p>
            <small>Use a natural photo.</small>
          </div>
          <div>
            <ShieldCheck size={18} />
            <p>Private</p>
            <small>Only used for verification.</small>
          </div>
        </div>
      </div>

      {error && <div className="doctor-form-error">{error}</div>}

      <div className="doctor-action-grid">
        <button
          type="button"
          onClick={prevStep}
          disabled={loader}
          className="doctor-secondary-action"
        >
          <ArrowLeft size={17} />
          Back
        </button>

        {!preview ? (
          <button
            type="button"
            onClick={capturePhoto}
            className="doctor-primary-action"
          >
            <Camera size={18} />
            Capture photo
          </button>
        ) : (
          <div className="doctor-action-grid is-nested">
            <button
              type="button"
              onClick={handleRetake}
              disabled={loader}
              className="doctor-secondary-action"
            >
              <RefreshCcw size={17} />
              Retake
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loader}
              className="doctor-primary-action"
            >
              {loader ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadCloud size={18} />
                  Submit
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="doctor-note-card">
        <ShieldCheck size={18} />
        <div>
          <p>Secure identity check</p>
          <small>
            Your photo is encrypted and used only to verify your doctor profile
            before dashboard access.
          </small>
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default StepThreeLivePhoto;
